import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Clock, ShieldCheck, Sparkles, RefreshCw } from "lucide-react";
import { api, apiError, num, shortDate, dateTime } from "@/lib/api";
import { AppShell, PageHeader, Panel, EmptyState, SkeletonRows, Btn, Tag } from "@/components/Shell";

export default function Digest() {
  const [digest, setDigest] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [d, h] = await Promise.all([
      api.get("/digest/preview"),
      api.get("/digest/history", { params: { limit: 10 } }),
    ]);
    setDigest(d.data);
    setHistory(h.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/digest/run-now");
      toast.success(`Digest generated — ${data.total_gaps} shortfall(s)`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Daily gap digest"
        subtitle="Every shortfall with the substitutes your team actually uses — built automatically at 07:00 UK each morning so buyers act before a stockout."
        testId="digest-header"
        actions={
          <Btn variant="outline" data-testid="digest-run-now-button" disabled={busy} onClick={runNow}>
            <RefreshCw className="h-4 w-4" /> {busy ? "Building…" : "Build today's digest"}
          </Btn>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3 border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600" data-testid="digest-schedule-note">
        <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" /> Runs daily at 07:00 Europe/London</span>
        <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-slate-400" /> Delivered in-app to Admins and Managers</span>
        <span className="text-xs text-slate-500">Email sending is not connected yet — turn it on whenever you're ready.</span>
      </div>

      {!digest ? (
        <div className="border border-slate-200 bg-white"><SkeletonRows rows={5} /></div>
      ) : (
        <>
          <div className="mb-4 grid gap-4 sm:grid-cols-4">
            {[
              ["Shortfalls", digest.total_gaps, "text-red-600", "digest-total-gaps"],
              ["Covered by substitutes", digest.covered_by_substitutes, "text-emerald-600", "digest-covered"],
              ["Uncovered", digest.uncovered, "text-slate-900", "digest-uncovered"],
              ["With learned swaps", digest.with_learned_substitutes, "text-violet-600", "digest-learned"],
            ].map(([label, value, tone, testId]) => (
              <div key={label} className="border border-slate-200 bg-white p-4">
                <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
                <p className={`num mt-1 text-2xl font-medium ${tone}`} data-testid={testId}>{num(value)}</p>
              </div>
            ))}
          </div>

          <Panel title={`Today's digest · ${dateTime(digest.generated_at)}`} testId="digest-preview-panel">
            {digest.items.length === 0 ? (
              <EmptyState icon={ShieldCheck} title="Nothing to chase" body="No SKU is oversold or below its reorder point right now." testId="digest-empty" />
            ) : (
              <div className="divide-y divide-slate-100">
                {digest.items.map((item) => (
                  <div key={item.sku} className="p-4" data-testid={`digest-item-${item.sku}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="mono text-sm">{item.sku}</p>
                        <p className="text-sm text-slate-700">{item.name}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Cause: {(item.cause || "—").replace(/[:_]/g, " ")}
                          {item.incoming > 0 && ` · ${num(item.incoming)} incoming, ETA ${shortDate(item.incoming_eta)}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-5 text-right">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-slate-500">Short by</p>
                          <p className="num text-lg text-red-600">{num(item.shortfall)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-slate-500">Available</p>
                          <p className="num text-lg" style={{ color: item.available < 0 ? "#EF4444" : "#10B981" }}>{num(item.available)}</p>
                        </div>
                        <Tag tone={item.covered_by_alternatives ? "green" : "red"}>
                          {item.covered_by_alternatives ? "Substitute cover" : "Needs a PO"}
                        </Tag>
                      </div>
                    </div>

                    {item.substitutes.length > 0 ? (
                      <ul className="mt-3 space-y-1.5 border-l-2 border-slate-200 pl-3 text-sm">
                        {item.substitutes.map((s) => (
                          <li key={s.sku} className="flex flex-wrap items-center gap-2" data-testid={`digest-sub-${item.sku}-${s.sku}`}>
                            <span className="mono text-xs">{s.sku}</span>
                            <span className="text-xs text-slate-500">covers {num(s.adjusted_cover)} at ratio {s.ratio}</span>
                            {s.auto_applied ? (
                              <Tag tone="violet"><Sparkles className="mr-1 inline h-3 w-3" />Auto {Math.round((s.confidence || 0) * 100)}%</Tag>
                            ) : s.learned ? (
                              <Tag tone="blue">Learned · chosen {num(s.times_chosen)}×</Tag>
                            ) : (
                              <Tag tone="slate">Configured</Tag>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-3 text-xs text-slate-500">
                        No substitute on file — <Link to="/app/gaps" className="underline">teach the engine</Link> what your team swaps for.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Digest history" testId="digest-history-panel" className="mt-4">
            {history.length === 0 ? (
              <EmptyState title="No digests generated yet" body="The 07:00 job will drop the first one in here." testId="digest-history-empty" />
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Generated</th>
                    <th className="px-3 py-2 text-right">Shortfalls</th>
                    <th className="px-3 py-2 text-right">Uncovered</th>
                    <th className="px-3 py-2 text-right">Learned swaps</th>
                    <th className="px-3 py-2 text-left">Recipients</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map((h) => (
                    <tr key={h.id} data-testid={`digest-history-row-${h.id}`}>
                      <td className="px-4 py-2 text-xs text-slate-600">{dateTime(h.generated_at)}</td>
                      <td className="num px-3 py-2 text-right">{num(h.total_gaps)}</td>
                      <td className="num px-3 py-2 text-right">{num(h.uncovered)}</td>
                      <td className="num px-3 py-2 text-right text-violet-600">{num(h.with_learned_substitutes)}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{h.recipients?.join(", ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </>
      )}
    </AppShell>
  );
}
